import { useColorMode } from '@chakra-ui/react';
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getCustomerFormPackage, updateCustomerFormPackage } from '../../redux/actions/package';
import { useUser } from 'contexts';

const businessTypes = [
   { value: 1, label: 'Mobile' },
   { value: 2, label: 'Electronic' },
];

const EditBusinessModal = ({ isOpen, onClose, initialData, onSubmit }) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const [formData, setFormData] = useState(initialData);
   const [isLoading, setIsLoading] = useState(false);
   const [errors, setErrors] = useState({});
   const [showModal, setShowModal] = useState(false);
   const cityData = useSelector((state) => state.city?.list || []);
   const dispatch = useDispatch();

   // Custom Dropdown States
   const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
   const [citySearchTerm, setCitySearchTerm] = useState('');
   const cityDropdownRef = useRef(null);
   useEffect(() => {
      if (isOpen) {
         setFormData(initialData);
         setShowModal(true);
      } else {
         setTimeout(() => setShowModal(false), 300);
      }
   }, [isOpen, initialData]);

   useEffect(() => {
      const handleEscKey = (event) => {
         if (event.key === 'Escape' && isOpen) {
            onClose();
         }
      };

      document.addEventListener('keydown', handleEscKey);

      return () => {
         document.removeEventListener('keydown', handleEscKey);
      };
   }, [isOpen, onClose]);

   // Close dropdown when clicking outside
   useEffect(() => {
      const handleClickOutside = (event) => {
         if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
            setIsCityDropdownOpen(false);
            setCitySearchTerm('');
         }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
   }, []);

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: name === 'cityId' || name === 'subBusinessType' ? parseInt(value) : value,
      }));

      if (errors[name]) {
         setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
   };

   const handleCitySelect = (cityId) => {
      setFormData((prev) => ({
         ...prev,
         cityId: cityId,
      }));
      setIsCityDropdownOpen(false);
      setCitySearchTerm('');

      if (errors.cityId) {
         setErrors((prev) => ({ ...prev, cityId: undefined }));
      }
   };

   const handleSubmit = async () => {
      setIsLoading(true);

      try {
         const submitData = {
            name: formData.name,
            address: formData.address || '',
            cityId: formData.cityId || null,
            gstin: formData.gstin,
            contactName: formData.contactName,
            contactMobile: formData.contactMobile,
            subBusinessType: formData.subBusinessType || 1,
         };

         const result = await dispatch(updateCustomerFormPackage(submitData));

         if (result && result.success) {
            await dispatch(getCustomerFormPackage());

            onClose();
         } else {
         }
      } catch (error) {
         console.error('Error in handleSubmit:', error);
      } finally {
         setIsLoading(false);
      }
   };

   const handleReset = () => {
      setFormData(initialData);
      setErrors({});
   };

   // Filter cities based on search term
   const filteredCities = cityData.filter((city) => city.name.toLowerCase().includes(citySearchTerm.toLowerCase()));

   // Get selected city name
   const selectedCityName = cityData.find((city) => city.id === formData.cityId)?.name || '';

   if (!showModal) return null;

   return (
      <>
         {/* Backdrop */}
         <div
            className={`fixed inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60 backdrop-blur-md z-50 transition-opacity duration-300 ${
               isOpen ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={onClose}
         />

         {/* Modal */}
         <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
               className={`w-full max-w-2xl transform transition-all duration-300 ${
                  isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
               }`}
               onClick={(e) => e.stopPropagation()}>
               <div
                  className={`rounded-2xl shadow-2xl overflow-hidden ${
                     isDark
                        ? 'bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800'
                        : 'bg-gradient-to-br from-white via-gray-50 to-white'
                  }`}>
                  {/* Decorative top border */}
                  <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

                  {/* Modal Header */}
                  <div
                     className={`relative px-8 pt-8 pb-6 ${
                        isDark ? 'border-b border-slate-700/50' : 'border-b border-gray-200/50'
                     }`}>
                     <div className="flex items-start justify-between">
                        <div className="flex-1">
                           <div className="flex items-center gap-3 mb-2">
                              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                                 <svg
                                    className="w-6 h-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                    />
                                 </svg>
                              </div>
                              <div>
                                 <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    Edit Business Details
                                 </h2>
                                 <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                                    Update your business information with ease
                                 </p>
                              </div>
                           </div>
                        </div>
                        <button
                           onClick={onClose}
                           className={`p-2.5 rounded-xl transition-all duration-200 hover:scale-110 ${
                              isDark
                                 ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                                 : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                           }`}>
                           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth={2}
                                 d="M6 18L18 6M6 6l12 12"
                              />
                           </svg>
                        </button>
                     </div>
                  </div>

                  {/* Modal Body */}
                  <div className="px-6 py-4">
                     <div className="grid md:grid-cols-2 gap-6 max-h-[60vh] overflow-y-auto pl-2 pr-2 custom-scrollbar">
                        {/* Business Name */}
                        <div className="md:col-span-2 group">
                           <label
                              className={`flex items-center gap-2 text-sm font-semibold mb-2 ${
                                 isDark ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                              Business Name *
                           </label>
                           <input
                              type="text"
                              name="name"
                              value={formData.name || ''}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 ${
                                 isDark
                                    ? 'bg-slate-800/50 border-slate-700 text-white focus:border-blue-500 focus:bg-slate-800'
                                    : 'bg-white border-gray-200 text-gray-900 focus:border-blue-500 focus:bg-blue-50/30'
                              } ${errors.name ? 'border-red-500 shake' : ''} focus:outline-none focus:ring-4 ${
                                 isDark ? 'focus:ring-blue-500/20' : 'focus:ring-blue-500/10'
                              }`}
                              placeholder="Enter your business name"
                           />
                           {errors.name && (
                              <p className="text-red-500 text-xs mt-2 flex items-center gap-1 animate-slideIn">
                                 <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                       fillRule="evenodd"
                                       d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                       clipRule="evenodd"
                                    />
                                 </svg>
                                 {errors.name}
                              </p>
                           )}
                        </div>

                        {/* Address */}
                        <div className="md:col-span-2">
                           <label
                              className={`flex items-center gap-2 text-sm font-semibold mb-2 ${
                                 isDark ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500" />
                              Address
                           </label>
                           <textarea
                              name="address"
                              value={formData.address || ''}
                              onChange={handleChange}
                              rows={3}
                              className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 resize-none ${
                                 isDark
                                    ? 'bg-slate-800/50 border-slate-700 text-white focus:border-purple-500 focus:bg-slate-800'
                                    : 'bg-white border-gray-200 text-gray-900 focus:border-purple-500 focus:bg-purple-50/30'
                              } focus:outline-none focus:ring-4 ${
                                 isDark ? 'focus:ring-purple-500/20' : 'focus:ring-purple-500/10'
                              }`}
                              placeholder="Enter complete business address"
                           />
                        </div>

                        {/* City - Custom Dropdown */}
                        <div>
                           <label
                              className={`flex items-center gap-2 text-sm font-semibold mb-2 ${
                                 isDark ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-pink-500 to-orange-500" />
                              City
                           </label>

                           <div ref={cityDropdownRef} className="relative">
                              <button
                                 type="button"
                                 onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
                                 className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 flex items-center justify-between ${
                                    isDark
                                       ? 'bg-slate-800/50 border-slate-700 text-white hover:border-pink-500'
                                       : 'bg-white border-gray-200 text-gray-900 hover:border-pink-500 shadow-sm'
                                 } focus:outline-none focus:ring-4 ${
                                    isDark ? 'focus:ring-pink-500/20' : 'focus:ring-pink-500/10'
                                 } ${
                                    isCityDropdownOpen
                                       ? isDark
                                          ? 'border-pink-500 ring-4 ring-pink-500/20'
                                          : 'border-pink-500 ring-4 ring-pink-500/10'
                                       : ''
                                 }`}>
                                 <span
                                    className={
                                       selectedCityName
                                          ? isDark
                                             ? 'text-white'
                                             : 'text-gray-900'
                                          : isDark
                                          ? 'text-slate-400'
                                          : 'text-gray-500'
                                    }>
                                    {selectedCityName || 'Select a city'}
                                 </span>
                                 <svg
                                    className={`w-4 h-4 transition-transform duration-200 ${
                                       isCityDropdownOpen ? 'rotate-180' : ''
                                    } ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M19 9l-7 7-7-7"
                                    />
                                 </svg>
                              </button>

                              {isCityDropdownOpen && (
                                 <div
                                    className={`absolute z-[60] w-full mt-2 rounded-xl border-2 shadow-2xl overflow-hidden animate-slideDown ${
                                       isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                                    }`}>
                                    {/* Search Box */}
                                    <div
                                       className={`p-3 sticky top-0 ${
                                          isDark
                                             ? 'bg-slate-800 border-b-2 border-slate-700'
                                             : 'bg-white border-b-2 border-gray-100'
                                       }`}>
                                       <div
                                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                                             isDark
                                                ? 'bg-slate-900/50 border-slate-700 focus-within:border-pink-500'
                                                : 'bg-gray-50 border-gray-200 focus-within:border-pink-500'
                                          }`}>
                                          <svg
                                             className={`w-4 h-4 flex-shrink-0 ${
                                                isDark ? 'text-slate-400' : 'text-gray-400'
                                             }`}
                                             fill="none"
                                             stroke="currentColor"
                                             viewBox="0 0 24 24">
                                             <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                             />
                                          </svg>
                                          <input
                                             type="text"
                                             placeholder="Search city..."
                                             value={citySearchTerm}
                                             onChange={(e) => setCitySearchTerm(e.target.value)}
                                             className={`flex-1 bg-transparent border-none outline-none text-sm ${
                                                isDark
                                                   ? 'text-white placeholder-slate-400'
                                                   : 'text-gray-900 placeholder-gray-500'
                                             }`}
                                             onClick={(e) => e.stopPropagation()}
                                             autoFocus
                                          />
                                          {citySearchTerm && (
                                             <button
                                                type="button"
                                                onClick={(e) => {
                                                   e.stopPropagation();
                                                   setCitySearchTerm('');
                                                }}
                                                className={`p-1 rounded hover:bg-slate-700/50 transition-colors ${
                                                   isDark
                                                      ? 'text-slate-400 hover:text-white'
                                                      : 'text-gray-400 hover:text-gray-600'
                                                }`}>
                                                <svg
                                                   className="w-3 h-3"
                                                   fill="none"
                                                   stroke="currentColor"
                                                   viewBox="0 0 24 24">
                                                   <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M6 18L18 6M6 6l12 12"
                                                   />
                                                </svg>
                                             </button>
                                          )}
                                       </div>
                                    </div>

                                    {/* Options List */}
                                    <div className="max-h-64 overflow-y-auto dropdown-scrollbar">
                                       {filteredCities.length > 0 ? (
                                          filteredCities.map((city) => (
                                             <button
                                                key={city.id}
                                                type="button"
                                                onClick={() => handleCitySelect(city.id)}
                                                className={`w-full px-4 py-2.5 text-left text-sm transition-all duration-150 flex items-center gap-3 ${
                                                   formData.cityId === city.id
                                                      ? isDark
                                                         ? 'bg-pink-500/20 text-pink-300 font-medium'
                                                         : 'bg-pink-50 text-pink-600 font-medium'
                                                      : isDark
                                                      ? 'text-slate-200 hover:bg-slate-700/70'
                                                      : 'text-gray-700 hover:bg-gray-50'
                                                }`}>
                                                {formData.cityId === city.id && (
                                                   <svg
                                                      className="w-4 h-4 flex-shrink-0 text-pink-500"
                                                      fill="currentColor"
                                                      viewBox="0 0 20 20">
                                                      <path
                                                         fillRule="evenodd"
                                                         d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                         clipRule="evenodd"
                                                      />
                                                   </svg>
                                                )}
                                                <span className="flex-1">{city.name}</span>
                                             </button>
                                          ))
                                       ) : (
                                          <div
                                             className={`px-4 py-8 text-center ${
                                                isDark ? 'text-slate-400' : 'text-gray-500'
                                             }`}>
                                             <svg
                                                className="w-12 h-12 mx-auto mb-3 opacity-40"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path
                                                   strokeLinecap="round"
                                                   strokeLinejoin="round"
                                                   strokeWidth={2}
                                                   d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                             </svg>
                                             <p className="text-sm font-medium">No cities found</p>
                                             <p className="text-xs mt-1 opacity-60">Try a different search term</p>
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              )}
                           </div>
                        </div>

                        {/* Business Type */}
                        <div>
                           <label
                              className={`flex items-center gap-2 text-sm font-semibold mb-2 ${
                                 isDark ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500" />
                              Business Type
                           </label>
                           <select
                              name="subBusinessType"
                              value={formData.subBusinessType || 1}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                                 isDark
                                    ? 'bg-slate-800/50 border-slate-700 text-white focus:border-orange-500 focus:bg-slate-800'
                                    : 'bg-white border-gray-200 text-gray-900 focus:border-orange-500 focus:bg-orange-50/30'
                              } focus:outline-none focus:ring-4 ${
                                 isDark ? 'focus:ring-orange-500/20' : 'focus:ring-orange-500/10'
                              }`}>
                              {businessTypes.map((type) => (
                                 <option
                                    key={type.value}
                                    value={type.value}
                                    className={isDark ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'}>
                                    {type.label}
                                 </option>
                              ))}
                           </select>
                        </div>

                        {/* GSTIN */}
                        <div>
                           <label
                              className={`flex items-center gap-2 text-sm font-semibold mb-2 ${
                                 isDark ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-yellow-500 to-green-500" />
                              GSTIN *
                           </label>
                           <input
                              type="text"
                              name="gstin"
                              value={formData.gstin || ''}
                              onChange={(e) => {
                                 // Convert to uppercase and update form data
                                 const uppercaseValue = e.target.value.toUpperCase();
                                 handleChange({
                                    target: {
                                       name: e.target.name,
                                       value: uppercaseValue,
                                    },
                                 });
                              }}
                              className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 font-mono ${
                                 isDark
                                    ? 'bg-slate-800/50 border-slate-700 text-white focus:border-yellow-500 focus:bg-slate-800'
                                    : 'bg-white border-gray-200 text-gray-900 focus:border-yellow-500 focus:bg-yellow-50/30'
                              } ${errors.gstin ? 'border-red-500 shake' : ''} focus:outline-none focus:ring-4 ${
                                 isDark ? 'focus:ring-yellow-500/20' : 'focus:ring-yellow-500/10'
                              }`}
                           />
                           {errors.gstin && (
                              <p className="text-red-500 text-xs mt-2 flex items-center gap-1 animate-slideIn">
                                 <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                       fillRule="evenodd"
                                       d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                       clipRule="evenodd"
                                    />
                                 </svg>
                                 {errors.gstin}
                              </p>
                           )}
                        </div>

                        {/* Contact Mobile */}
                        <div>
                           <label
                              className={`flex items-center gap-2 text-sm font-semibold mb-2 ${
                                 isDark ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-green-500 to-teal-500" />
                              Contact Mobile *
                           </label>
                           <input
                              type="tel"
                              name="contactMobile"
                              value={formData.contactMobile || ''}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 ${
                                 isDark
                                    ? 'bg-slate-800/50 border-slate-700 text-white focus:border-green-500 focus:bg-slate-800'
                                    : 'bg-white border-gray-200 text-gray-900 focus:border-green-500 focus:bg-green-50/30'
                              } ${errors.contactMobile ? 'border-red-500 shake' : ''} focus:outline-none focus:ring-4 ${
                                 isDark ? 'focus:ring-green-500/20' : 'focus:ring-green-500/10'
                              }`}
                           />
                           {errors.contactMobile && (
                              <p className="text-red-500 text-xs mt-2 flex items-center gap-1 animate-slideIn">
                                 <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                       fillRule="evenodd"
                                       d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                       clipRule="evenodd"
                                    />
                                 </svg>
                                 {errors.contactMobile}
                              </p>
                           )}
                        </div>

                        {/* Contact Name */}
                        <div className="md:col-span-2">
                           <label
                              className={`flex items-center gap-2 text-sm font-semibold mb-2 ${
                                 isDark ? 'text-slate-300' : 'text-gray-700'
                              }`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500" />
                              Contact Person *
                           </label>
                           <input
                              type="text"
                              name="contactName"
                              value={formData.contactName || ''}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 ${
                                 isDark
                                    ? 'bg-slate-800/50 border-slate-700 text-white focus:border-teal-500 focus:bg-slate-800'
                                    : 'bg-white border-gray-200 text-gray-900 focus:border-teal-500 focus:bg-teal-50/30'
                              } ${errors.contactName ? 'border-red-500 shake' : ''} focus:outline-none focus:ring-4 ${
                                 isDark ? 'focus:ring-teal-500/20' : 'focus:ring-teal-500/10'
                              }`}
                              placeholder="Enter contact person name"
                           />
                           {errors.contactName && (
                              <p className="text-red-500 text-xs mt-2 flex items-center gap-1 animate-slideIn">
                                 <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                       fillRule="evenodd"
                                       d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                       clipRule="evenodd"
                                    />
                                 </svg>
                                 {errors.contactName}
                              </p>
                           )}
                        </div>
                     </div>
                  </div>

                  {/* Modal Footer */}
                  <div
                     className={`px-8 py-6 ${
                        isDark
                           ? 'bg-slate-900/50 border-t border-slate-700/50'
                           : 'bg-gray-50/50 border-t border-gray-200/50'
                     }`}>
                     <div className="flex justify-end gap-3">
                        <button
                           onClick={handleReset}
                           className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 hover:scale-105 ${
                              isDark
                                 ? 'text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700'
                                 : 'text-gray-700 hover:text-gray-900 bg-white hover:bg-gray-100 border border-gray-300'
                           }`}>
                           Reset
                        </button>
                        <button
                           onClick={onClose}
                           className={`px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 hover:scale-105 ${
                              isDark
                                 ? 'text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700'
                                 : 'text-gray-700 hover:text-gray-900 bg-white hover:bg-gray-100 border border-gray-300'
                           }`}>
                           Cancel
                        </button>
                        <button
                           onClick={handleSubmit}
                           disabled={isLoading}
                           className={`px-8 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl ${
                              isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105'
                           } bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white`}>
                           {isLoading ? (
                              <>
                                 <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle
                                       className="opacity-25"
                                       cx="12"
                                       cy="12"
                                       r="10"
                                       stroke="currentColor"
                                       strokeWidth="4"
                                    />
                                    <path
                                       className="opacity-75"
                                       fill="currentColor"
                                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                 </svg>
                                 Saving...
                              </>
                           ) : (
                              <>
                                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M5 13l4 4L19 7"
                                    />
                                 </svg>
                                 Save Changes
                              </>
                           )}
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         <style>{`
            @keyframes slideIn {
               from {
                  opacity: 0;
                  transform: translateX(-10px);
               }
               to {
                  opacity: 1;
                  transform: translateX(0);
               }
            }
            @keyframes shake {
               0%, 100% { transform: translateX(0); }
               25% { transform: translateX(-5px); }
               75% { transform: translateX(5px); }
            }
            .animate-slideIn {
               animation: slideIn 0.3s ease-out;
            }
            .shake {
               animation: shake 0.3s ease-out;
            }
            .custom-scrollbar::-webkit-scrollbar {
               width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
               background: transparent;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
               background: linear-gradient(180deg, #3b82f6, #8b5cf6);
               border-radius: 10px;
            }
         `}</style>
      </>
   );
};

export default EditBusinessModal;
